import { isNotNullable, isString } from '@bayer-int/portletce-shared';
import { isEmpty } from 'lodash-es';

import { queryReplacer } from 'portlet/models/query/systems/databricks/QueryReplacer';
import { QueryChartInteractionDatapointUtil } from 'portlet/services/InteractionToFilterService/QueryChartInteractionDatapoint';
import { FilterValue } from 'portlet/models/filterValue';

import type { DatabricksParameters, DatabricksQuery} from './types';
import { DatabricksQueryC } from './types';
import type { PortletQueryDefinition } from '../types';

export const DatabricksDefinitionC: PortletQueryDefinition<DatabricksQuery, DatabricksParameters> =
    {
        codec: DatabricksQueryC,
        build: (metadata, settings, filters) => {
            const statement = queryReplacer(metadata.statement, metadata.tables, filters, settings);

            return {
                statement,
                metadata,
                filters,
            };
        },
        externalLink: () => {
            return null;
        },
        interactionToFilter: (metadata, listenTo, datapoint) => {
            const filtersInteractedWith = metadata.tables
                .map(table => table.columns)
                .flat()
                .filter(col => col.name === datapoint.columnKey && col.filter?.listenTo);
            const filterKeys = filtersInteractedWith
                .map(
                    column =>
                        column.filter?.listenTo &&
                        splitToHierarchyDotNotation(column.filter?.listenTo).filterKey,
                )
                .filter(isNotNullable);

            if (isEmpty(filterKeys)) return null;
            const matchedListenTos = listenTo.filter(({ item }) => filterKeys.includes(item));
            const matchedListenTo = matchedListenTos[0];
            if (!matchedListenTo || !matchedListenTo.interactionEnabled) return null;

            if (matchedListenTo.columnMapping) {
                const columnKeysToMap = Object.keys(matchedListenTo.columnMapping);
                const columnMappedValue = FilterValue.DIMENSION({
                    exclude: false,
                    value: QueryChartInteractionDatapointUtil.toMultiSelect(datapoint).items.map(
                        item => {
                            if (columnKeysToMap.includes(item.columnKey) && item.datapoint) {
                                const columnMapping =
                                    matchedListenTo.columnMapping &&
                                    matchedListenTo.columnMapping[item.columnKey];
                                const mappedKey =
                                    isNotNullable(columnMapping?.key) &&
                                    item.datapoint[columnMapping.key];
                                const mappedValue =
                                    isNotNullable(columnMapping?.value) &&
                                    item.datapoint[columnMapping.value];

                                return {
                                    key: isNotNullable(mappedKey) ? String(mappedKey) : item.key,
                                    value: isNotNullable(mappedValue)
                                        ? String(mappedValue)
                                        : item.key,
                                    identifier: isString(mappedKey) ? mappedKey : item.key,
                                    internalKey:
                                        matchedListenTo.filterHint?.dimension?.internalKey || '',
                                    characteristicId: '',
                                    basedOnInternalKey:
                                        matchedListenTo.filterHint?.dimension?.internalKey || '',
                                    basedOnCharacteristicId: '',
                                };
                            }

                            return {
                                key: item.key,
                                value: item.value,
                                identifier: item.key,
                                internalKey:
                                    matchedListenTo.filterHint?.dimension?.internalKey || '',
                                characteristicId: '',
                                basedOnInternalKey:
                                    matchedListenTo.filterHint?.dimension?.internalKey || '',
                                basedOnCharacteristicId: '',
                            };
                        },
                    ),
                });
                return { item: filterKeys[0], value: columnMappedValue };
            }

            const value = FilterValue.DIMENSION({
                exclude: false,
                value: QueryChartInteractionDatapointUtil.toMultiSelect(datapoint).items.map(
                    ({ key, value }) => ({
                        key: key,
                        value: value,
                        // TODO: following fields are not probably redundant, and we can only create this filter value
                        // in a best-effort manner. For precise and correct values consider using filter hints.
                        identifier: key,
                        internalKey: matchedListenTo.filterHint?.dimension?.internalKey || '',
                        characteristicId: '',
                        basedOnInternalKey:
                            matchedListenTo.filterHint?.dimension?.internalKey || '',
                        basedOnCharacteristicId: '',
                    }),
                ),
            });

            return { item: filterKeys[0], value };
        },
        interactionToListenTo: () => null,
    };

function splitToHierarchyDotNotation(to: string): {
    filterKey: string;
    hierarchyInternalKey: string | undefined;
} {
    const [filterKey, hierarchyInternalKey] = to.split('.');

    return { filterKey, hierarchyInternalKey };
}
